import { getAuthToken, getStoredUser, getAuthHeaders } from '@/utils/auth';
import { clearAuth } from '@/utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StyledSelect } from '@/components/ui/styled-select';
import { 
  Car, TrendingUp, DollarSign, Plus, X, LogOut, Package, BarChart3, Users, ArrowLeft, User, Pencil, Trash2, CheckSquare, Square
} from 'lucide-react';
import Nav from '@/components/Nav';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '@/context/CurrencyContext';
import PartnerCRM from '@/components/PartnerCRM';
import PartnerCRMDashboardV2 from '@/components/PartnerCRMDashboardV2';
import AssetMediaManager from '@/components/partner/AssetMediaManager';
import LocationField from '@/components/partner/LocationField';
import BulkUploadModal from '@/components/partner/BulkUploadModal';
import { CAR_BRANDS, CAR_MODELS, CAR_COLORS, CAR_CONDITIONS, CAR_YEARS } from '@/data/carData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Luxury Car Background Images
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1657197576778-1b14b6c2834a?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1657197580413-d6bb79e9f793?auto=format&fit=crop&w=1920&q=80',
  'https://images.pexels.com/photos/35113540/pexels-photo-35113540.jpeg?auto=compress&cs=tinysrgb&w=1920'
];

const CarDealerDashboard = () => {
  const navigate = useNavigate();
  const { formatPrice: formatCurrency } = useCurrency();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('inventory');
  const [cars, setCars] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [editingCar, setEditingCar] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  // Per-listing completion-% for v2 wizard cars (keyed by car id).
  const [completion, setCompletion] = useState({});
  // Cached current asset id while in edit mode — needed by AssetMediaManager.
  const editingId = editingCar?.id || null;

  const [newCar, setNewCar] = useState({
    brand: '', model: '', year: new Date().getFullYear(), price: 500000,
    mileage: 0, color: '', condition: 'new', description: '',
    location: '', latitude: '', longitude: '',
    images: [], videos: [],
  });

  const handleLogout = useCallback(() => {
    clearAuth();
    navigate('/partner/login');
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [carsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/partner/cars`, { headers }),
        axios.get(`${API}/partner/crm/analytics`, { headers })
      ]);
      setCars(carsRes.data);
      setAnalytics(analyticsRes.data);

      // Fetch completion-% for every car (legacy + v2 wizard).
      const all = carsRes.data || [];
      if (all.length === 0) {
        setCompletion({});
      } else {
        const entries = await Promise.all(all.map(async (c) => {
          try {
            const { data } = await axios.post(
              `${API}/partner-listings/car/${c.id}/validate`,
              null,
              { headers },
            );
            return [c.id, data];
          } catch { return [c.id, null]; }
        }));
        const map = {};
        entries.forEach(([id, rep]) => { if (rep) map[id] = rep; });
        setCompletion(map);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    const userData = JSON.stringify(getStoredUser());
    if (userData) setUser(JSON.parse(userData));
    fetchData();
    
    // Rotate background images
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleCreateCar = async () => {
    try {
      const token = getAuthToken();
      if (editingCar) {
        await axios.put(`${API}/partner/cars/${editingCar.id}`, newCar, { headers: getAuthHeaders() });
        toast.success('Car updated!');
        setShowAddModal(false);
        setEditingCar(null);
      } else {
        // Create the car first; only then can we upload media (which requires the new id).
        // We open the form again with the new asset id so the partner can attach media.
        const res = await axios.post(`${API}/partner/cars`, newCar, { headers: getAuthHeaders() });
        toast.success('Car added — you can now upload images/videos.');
        setEditingCar(res.data);
        setNewCar(p => ({ ...p, images: res.data.images || [], videos: res.data.videos || [] }));
        // Keep modal open so the partner can immediately attach media.
        fetchData();
        return;
      }
      setNewCar({
        brand: '', model: '', year: new Date().getFullYear(), price: 500000, mileage: 0,
        color: '', condition: 'new', description: '',
        location: '', latitude: '', longitude: '', images: [], videos: [],
      });
      fetchData();
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditCar = (car) => {
    // v2 listings (asset_type='car') open in the new wizard
    if (car.asset_type) {
      navigate(`/partner/listings/car/${car.id}`);
      return;
    }
    setEditingCar(car);
    setNewCar({
      brand: car.brand || '', model: car.model || '', year: car.year || new Date().getFullYear(),
      price: car.price || 0, mileage: car.mileage || 0, color: car.color || '',
      condition: car.condition || 'new', description: car.description || '',
      location: car.location || '', latitude: car.latitude ?? '', longitude: car.longitude ?? '',
      images: car.images || [], videos: car.videos || [],
    });
    setShowAddModal(true);
  };

  // Unified card adapter — handles v2 wizard + legacy schema
  const adaptCar = (c) => ({
    cover: c.cover_image
      || (Array.isArray(c.gallery_images) && c.gallery_images[0])
      || (Array.isArray(c.images) && c.images[0])
      || null,
    title: [c.brand, c.model].filter(Boolean).join(' ') || 'Untitled Car',
    variant: c.variant,
    year: c.year,
    condition: c.condition || c.ownership || 'Pre-owned',
    fuel: c.fuel_type,
    transmission: c.transmission,
    hp: c.horsepower,
    odometer: c.odometer ?? c.mileage,
    color: c.exterior_color || c.color,
    interior: c.interior_color,
    seats: c.seating_capacity,
    price: c.price,
    features: Array.isArray(c.features) ? c.features.length : 0,
    videos: Array.isArray(c.videos) ? c.videos.length : 0,
    gallery: Array.isArray(c.gallery_images) ? c.gallery_images.length : (Array.isArray(c.images) ? c.images.length : 0),
    status: c.status,
  });

  const handleDeleteCar = async (carId) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/partner/cars/${carId}`, { headers: getAuthHeaders() });
      toast.success('Car deleted');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete car');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === cars.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(cars.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    try {
      const token = getAuthToken();
      const ids = Array.from(selectedIds);
      await axios.post(`${API}/partner/cars/bulk-delete`, { ids }, { headers: getAuthHeaders() });
      toast.success(`${ids.length} cars deleted`);
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete cars');
    }
  };

  const handleUpdateCarStatus = async (carId, status) => {
    try {
      const token = getAuthToken();
      await axios.put(`${API}/partner/cars/${carId}`, { status }, { headers: getAuthHeaders() });
      toast.success('Status updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const totalInventoryValue = cars.filter(c => c.status === 'available').reduce((sum, c) => sum + (c.price || 0), 0);
  const availableCars = cars.filter(c => c.status === 'available').length;
  const soldCars = cars.filter(c => c.status === 'sold').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      {/* Navigation */}
      <Nav variant="partner" showBack partnerLabel="Car Dealer Portal" companyName={user?.company_name} />

      {/* Hero Section with Luxury Car Background */}
      <div className="relative h-[320px] overflow-hidden" data-testid="car-dealer-hero">
        {HERO_IMAGES.map((img, index) => (
          <motion.div
            key={img}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${img})` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentBgIndex ? 1 : 0 }}
            transition={{ duration: 1.5 }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-obsidian" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
        
        <div className="relative z-10 max-w-[1440px] mx-auto px-8 h-full flex items-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-500/20 backdrop-blur-sm flex items-center justify-center">
                <Car className="w-6 h-6 text-red-400" />
              </div>
              <span className="text-red-400/80 text-sm uppercase tracking-widest">Luxury Automotive</span>
            </div>
            <h1 className="font-playfair text-5xl font-semibold mb-3 text-white">Car Dealer Dashboard</h1>
            <p className="text-white/70 text-lg">Welcome back, <span className="text-gold">{user?.name}</span></p>
          </motion.div>
          
          {activeTab === 'inventory' && (
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="ml-auto flex gap-2"
            >
              <Button onClick={() => setBulkUploadOpen(true)} variant="outline" className="border-gold/30 text-gold hover:bg-gold/10" data-testid="bulk-upload-cars-btn">
                <Package className="w-4 h-4 mr-2" /> Bulk Upload
              </Button>
              <Button onClick={() => navigate('/partner/listings/new/car')} className="bg-gold text-black hover:bg-gold-light" data-testid="add-car-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Car
              </Button>
            </motion.div>
          )}
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-8 py-8 -mt-16 relative z-20">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-testid="car-stats">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-red-500/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-3xl group-hover:bg-red-500/20 transition-all" />
            <Car className="w-10 h-10 text-red-400 mb-4 relative z-10" />
            <div className="text-4xl font-bold mb-1 relative z-10">{cars.length}</div>
            <div className="text-text-secondary">Total Inventory</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-emerald-500/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl group-hover:bg-emerald-500/20 transition-all" />
            <Package className="w-10 h-10 text-emerald-400 mb-4 relative z-10" />
            <div className="text-4xl font-bold mb-1 relative z-10">{availableCars}</div>
            <div className="text-text-secondary">Available</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-gold/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-3xl group-hover:bg-gold/20 transition-all" />
            <TrendingUp className="w-10 h-10 text-gold mb-4 relative z-10" />
            <div className="text-4xl font-bold mb-1 relative z-10">{soldCars}</div>
            <div className="text-text-secondary">Sold</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-gold/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-3xl group-hover:bg-gold/20 transition-all" />
            <DollarSign className="w-10 h-10 text-gold mb-4 relative z-10" />
            <div className="text-2xl font-bold mb-1 relative z-10">{formatCurrency(totalInventoryValue)}</div>
            <div className="text-text-secondary">Inventory Value</div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 bg-white/5 backdrop-blur-sm rounded-xl p-2" data-testid="car-tabs">
          {['inventory', 'crm', 'reports'].map((tab) => (
            <button 
              key={tab} 
              onClick={() => setActiveTab(tab)} 
              data-testid={`tab-${tab}`}
              className={`px-6 py-3 rounded-lg transition-all duration-300 capitalize font-medium ${
                activeTab === tab 
                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/25' 
                  : 'text-text-secondary hover:bg-white/10 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'inventory' && (
          <div>
            {/* Bulk Actions Toolbar */}
            {cars.length > 0 && (
              <div className="flex items-center justify-between mb-6 glass-card p-4">
                <div className="flex items-center gap-4">
                  <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-text-secondary hover:text-white transition-colors" data-testid="select-all-cars">
                    {selectedIds.size === cars.length && cars.length > 0 ? <CheckSquare className="w-5 h-5 text-gold" /> : <Square className="w-5 h-5" />}
                    <span>{selectedIds.size === cars.length && cars.length > 0 ? 'Deselect All' : 'Select All'}</span>
                  </button>
                  {selectedIds.size > 0 && (
                    <span className="text-sm text-gold font-medium">{selectedIds.size} selected</span>
                  )}
                </div>
                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-2">
                    {bulkDeleteConfirm ? (
                      <>
                        <span className="text-sm text-red-400 mr-2">Delete {selectedIds.size} items?</span>
                        <Button size="sm" onClick={handleBulkDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-bulk-delete-cars">Yes, Delete</Button>
                        <Button size="sm" variant="outline" onClick={() => setBulkDeleteConfirm(false)} className="border-white/20 text-white hover:bg-white/10">Cancel</Button>
                      </>
                    ) : (
                      <Button size="sm" onClick={() => setBulkDeleteConfirm(true)} className="bg-red-600/80 text-white hover:bg-red-600" data-testid="bulk-delete-cars-btn">
                        <Trash2 className="w-4 h-4 mr-1.5" /> Delete Selected ({selectedIds.size})
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cars.map((car) => {
              const v = adaptCar(car);
              return (
              <div key={car.id} className={`glass-card overflow-hidden hover:border-gold/30 transition-colors relative group ${selectedIds.has(car.id) ? 'border-gold/50 bg-gold/5' : ''}`} data-testid={`car-card-${car.id}`}>
                {/* Cover */}
                <div className="relative h-44 bg-gradient-to-br from-white/5 to-white/[0.02]">
                  {v.cover ? (
                    <img src={v.cover} alt={v.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-text-secondary">
                      <Car className="w-10 h-10 opacity-30" />
                    </div>
                  )}
                  <button onClick={() => toggleSelect(car.id)} className="absolute top-3 right-3 z-10 bg-black/60 backdrop-blur p-1.5 rounded-md" data-testid={`select-car-${car.id}`}>
                    {selectedIds.has(car.id) ? <CheckSquare className="w-4 h-4 text-gold" /> : <Square className="w-4 h-4 text-white/70" />}
                  </button>
                  {/* Completion pill (every car) */}
                  {typeof completion[car.id]?.total_pct === 'number' && (
                    <span
                      className={`absolute top-3 left-3 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.15em] font-semibold ${
                        completion[car.id].total_pct >= 100 ? 'bg-gold text-black' : 'bg-black/70 backdrop-blur text-white/90 ring-1 ring-white/15'
                      }`}
                      data-testid={`completion-pill-${car.id}`}
                    >
                      {completion[car.id].total_pct}% complete
                    </span>
                  )}
                  {v.gallery > 0 && (
                    <span className="absolute bottom-3 right-3 bg-black/70 backdrop-blur px-2 py-0.5 rounded text-[10px] text-white/80">+{v.gallery} photos</span>
                  )}
                </div>

                <div className="p-5">
                  <div className="flex justify-between items-start gap-3 mb-3">
                    <div className="min-w-0">
                      <h4 className="font-semibold text-base line-clamp-1">{v.title}</h4>
                      <p className="text-text-secondary text-xs">{v.year} {v.variant && `· ${v.variant}`} · {v.condition}</p>
                    </div>
                    <StyledSelect
                      value={car.status}
                      onChange={(v) => handleUpdateCarStatus(car.id, v)}
                      options={[
                        { value: 'available', label: 'Available' },
                        { value: 'reserved', label: 'Reserved' },
                        { value: 'sold', label: 'Sold' },
                      ]}
                      className="text-xs h-8 px-2 flex-shrink-0 w-auto"
                      data-testid={`car-status-select-${car.id}`}
                    />
                  </div>

                  {/* Specs row */}
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-white/80 mb-2">
                    {v.fuel && <span>{v.fuel}</span>}
                    {v.transmission && <span>·</span>}
                    {v.transmission && <span>{v.transmission}</span>}
                    {v.hp && <span>·</span>}
                    {v.hp && <span><strong>{v.hp}</strong> hp</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-text-secondary">
                    {v.color && <div>Exterior: <span className="text-white/80">{v.color}</span></div>}
                    {v.interior && <div>Interior: <span className="text-white/80">{v.interior}</span></div>}
                    {v.odometer != null && <div>Odometer: <span className="text-white/80">{Number(v.odometer).toLocaleString()} km</span></div>}
                    {v.seats != null && <div>Seats: <span className="text-white/80">{v.seats}</span></div>}
                  </div>

                  {(v.features || v.videos) > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {v.features > 0 && <span className="px-2 py-0.5 rounded-full bg-white/5 text-[10px] text-white/70">{v.features} features</span>}
                      {v.videos > 0 && <span className="px-2 py-0.5 rounded-full bg-gold/10 text-[10px] text-gold">{v.videos} videos</span>}
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-white/10 flex items-baseline justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-text-secondary">Price</div>
                      <div className="text-gold text-xl font-semibold">{formatCurrency(v.price)}</div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                    <Button variant="outline" size="sm" onClick={() => handleEditCar(car)} className="flex-1 border-white/20 text-white hover:bg-white/10 hover:text-gold" data-testid={`edit-car-${car.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                    </Button>
                    {deleteConfirm === car.id ? (
                      <div className="flex gap-1 flex-1">
                        <Button size="sm" onClick={() => handleDeleteCar(car.id)} className="flex-1 bg-red-600 text-white hover:bg-red-700" data-testid={`confirm-delete-car-${car.id}`}>Yes</Button>
                        <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 border-white/20 text-white hover:bg-white/10">No</Button>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(car.id)} className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid={`delete-car-${car.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
            {cars.length === 0 && (
              <div className="col-span-3 glass-card p-12 text-center">
                <Car className="w-16 h-16 text-gold mx-auto mb-4 opacity-50" />
                <h3 className="font-playfair text-2xl font-semibold mb-2">No Cars Yet</h3>
                <p className="text-text-secondary mb-6">Add your first car to start your inventory</p>
                <Button onClick={() => navigate('/partner/listings/new/car')} className="bg-gold text-black hover:bg-gold-light">
                  <Plus className="w-4 h-4 mr-2" /> Add Car
                </Button>
              </div>
            )}
          </div>
          </div>
        )}

        {activeTab === 'crm' && (
          <div className="space-y-6">
            <PartnerCRMDashboardV2 />
            <PartnerCRM partnerId={user?.id} />
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="glass-card p-8 text-center">
            <BarChart3 className="w-16 h-16 text-gold mx-auto mb-4 opacity-50" />
            <h3 className="font-playfair text-2xl font-semibold mb-2">Reports Coming Soon</h3>
            <p className="text-text-secondary">Sales reports and analytics will be available soon.</p>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-lenis-prevent>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="glass-card p-8 max-w-lg w-full max-h-[85vh] overflow-y-auto overscroll-contain"
            style={{ scrollbarGutter: 'stable' }}
            data-lenis-prevent
          >
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-obsidian/90 backdrop-blur-sm pb-4 -mt-2 pt-2 z-10">
              <h2 className="font-playfair text-2xl font-semibold">{editingCar ? 'Edit Car' : 'Add New Car'}</h2>
              <Button variant="ghost" onClick={() => { setShowAddModal(false); setEditingCar(null); }}><X className="w-5 h-5" /></Button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Brand *</Label>
                  <StyledSelect
                    value={newCar.brand}
                    onChange={(v) => setNewCar({...newCar, brand: v, model: ''})}
                    placeholder="Select Brand"
                    options={CAR_BRANDS.map((brand) => ({ value: brand, label: brand }))}
                    data-testid="new-car-brand-select"
                  />
                </div>
                <div>
                  <Label>Model *</Label>
                  <StyledSelect
                    value={newCar.model}
                    onChange={(v) => setNewCar({...newCar, model: v})}
                    placeholder="Select Model"
                    disabled={!newCar.brand}
                    options={(newCar.brand && CAR_MODELS[newCar.brand] ? CAR_MODELS[newCar.brand] : []).map((model) => ({ value: model, label: model }))}
                    data-testid="new-car-model-select"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Year</Label>
                  <StyledSelect
                    value={String(newCar.year || '')}
                    onChange={(v) => setNewCar({...newCar, year: parseInt(v)})}
                    options={CAR_YEARS.map((year) => ({ value: String(year), label: String(year) }))}
                    data-testid="new-car-year-select"
                  />
                </div>
                <div>
                  <Label>Price (AED) *</Label>
                  <Input type="number" value={newCar.price} onChange={(e) => setNewCar({...newCar, price: parseFloat(e.target.value)})} className="bg-white text-black" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Mileage (km)</Label>
                  <Input type="number" value={newCar.mileage} onChange={(e) => setNewCar({...newCar, mileage: parseInt(e.target.value)})} className="bg-white text-black" />
                </div>
                <div>
                  <Label>Color</Label>
                  <StyledSelect
                    value={newCar.color}
                    onChange={(v) => setNewCar({...newCar, color: v})}
                    placeholder="Select Color"
                    options={CAR_COLORS.map((color) => ({ value: color.name, label: color.name }))}
                    data-testid="new-car-color-select"
                  />
                </div>
              </div>
              <div>
                <Label>Condition</Label>
                <StyledSelect
                  value={newCar.condition}
                  onChange={(v) => setNewCar({...newCar, condition: v})}
                  options={CAR_CONDITIONS.map((c) => ({ value: c.id, label: c.label }))}
                  data-testid="new-car-condition-select"
                />
              </div>
              <div>
                <Label>Description</Label>
                <textarea value={newCar.description} onChange={(e) => setNewCar({...newCar, description: e.target.value})} className="w-full bg-white text-black rounded p-3 min-h-[80px]" />
              </div>

              {/* Location & Map */}
              <div className="pt-2 border-t border-white/10">
                <LocationField
                  testIdPrefix="car"
                  location={newCar.location}
                  latitude={newCar.latitude}
                  longitude={newCar.longitude}
                  onChange={(loc) => setNewCar(p => ({ ...p, ...loc }))}
                />
              </div>

              {/* Media (image + video) — only available after the car is saved */}
              <div className="pt-2 border-t border-white/10">
                <Label className="text-text-primary mb-2 block">Photos &amp; Videos</Label>
                <AssetMediaManager
                  assetType="cars"
                  assetId={editingId}
                  images={newCar.images}
                  videos={newCar.videos}
                  onChange={(media) => setNewCar(p => ({ ...p, ...media }))}
                />
              </div>

              <div className="pt-4 pb-2">
                <Button onClick={handleCreateCar} disabled={!newCar.brand || !newCar.model} className="w-full bg-gold text-black hover:bg-gold-light" data-testid="save-car-btn">
                  {editingCar ? 'Update Car' : 'Add Car'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <BulkUploadModal
        open={bulkUploadOpen}
        assetType="cars"
        onClose={() => setBulkUploadOpen(false)}
        onComplete={fetchData}
      />
    </div>
  );
};

export default CarDealerDashboard;
